/**
 * News for the people it is written for (server: admin/news.py): the signed-in person's feed with
 * their read state, "mark as read", and news' part of the attention inbox — unread posts count in
 * the top bar's bell; Home shows them in a card of its own. Reading news is a personal permission
 * (`admin.news.read`): everyone signed in holds it for their own news.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { api, unwrap } from '@/lib/api/client';
import type { components } from '@/lib/api/schema';
import { msg, t } from '@/lib/i18n';
import type { InboxItem, InboxSource } from '@/lib/inbox';
import { Button } from '@/ui';

export type NewsFeedOut = components['schemas']['NewsFeedOut'];

export const newsFeedKeys = {
  all: ['admin', 'news', 'feed'] as const,
  feed: (unread: boolean) => ['admin', 'news', 'feed', unread ? 'unread' : 'all'] as const,
};

/** Scheduled posts go live on their own: look again now and then. */
const REFRESH = 10 * 60_000;

export const newsFeedQuery = (unread: boolean) => ({
  queryKey: newsFeedKeys.feed(unread),
  queryFn: () => unwrap(api.GET('/api/admin/news/feed', { params: { query: { unread, limit: 20 } } })),
  refetchInterval: REFRESH,
  staleTime: 60_000,
});

/** The person's feed: newest first, read and unread (`read_at`). */
export function useNewsFeed(enabled = true) {
  return useQuery({ ...newsFeedQuery(false), enabled });
}

export function useMarkNewsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => unwrap(api.POST('/api/admin/news/read', { body: { news_post_ids: ids } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: newsFeedKeys.all }),
  });
}

/** Plain text of a post's Markdown, for one or two lines of context. */
export function newsExcerpt(body: string, max = 240): string {
  const plain = body
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_`#>|]/g, '')
    .replace(/^\s*[-•]\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
  return plain.length > max ? `${plain.slice(0, max - 1).trimEnd()}…` : plain;
}

function MarkReadButton({ id }: { id: string }) {
  const mark = useMarkNewsRead();
  return (
    <Button size="sm" variant="ghost" icon={Check} loading={mark.isPending} onClick={() => mark.mutate([id])}>
      {t('Mark as read')}
    </Button>
  );
}

export const newsInbox: InboxSource<NewsFeedOut[]> = {
  key: 'news',
  label: msg('News'),
  order: 60,
  permission: 'admin.news.read',
  module: 'admin',
  bell: true,
  home: false, // Home shows unread news in a card of its own, with the whole text
  href: '/',
  query: () => newsFeedQuery(true),
  items: (rows) =>
    rows.map((n): InboxItem => ({
      id: `news:${n.id}`,
      source: 'news',
      kind: msg('News'),
      tone: 'info',
      title: n.title,
      detail: newsExcerpt(n.body) || null,
      meta: n.all_companies ? [t('From Toecan')] : undefined,
      at: n.published_at,
      href: `/?news=${n.id}`,
      rank: 6,
      actions: <MarkReadButton id={n.id} />,
    })),
};
